import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notificationsRef, db } from '../firebase/collections';
import { AuthContext } from './AuthContext';
import { ToastContext } from './ToastContext';

export const NotificationContext = createContext(null);

function NotificationProvider({ children }) {
  const { user } = React.useContext(AuthContext);
  const { addToast } = React.useContext(ToastContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const q = notificationsRef()
      .where('userId', '==', user.uid)
      .orderBy('createdAt', 'desc')
      .limit(50);
    const unsubscribe = q.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.()?.getTime?.() || 0,
        }));
        setNotifications(list);
        const unread = list.filter((n) => n.read !== true).length;
        setUnreadCount(unread);
      },
      (err) => {
        console.error('NotificationContext listener error:', err);
        addToast('Notificações temporariamente indisponíveis.', 'error');
        setNotifications([]);
        setUnreadCount(0);
      }
    );
    return () => unsubscribe();
  }, [user?.uid, addToast]);

  const markAsRead = useCallback(
    (notificationId) => {
      if (!notificationId || !user?.uid) return;
      const doc = notificationsRef().doc(notificationId);
      doc.get().then((snap) => {
        if (snap.exists && snap.data().userId === user.uid) {
          doc.update({ read: true });
        }
      });
    },
    [user?.uid]
  );

  const markAllRead = useCallback(() => {
    if (!user?.uid) return;
    const batch = db.batch();
    notifications
      .filter((n) => !n.read)
      .forEach((n) => {
        batch.update(notificationsRef().doc(n.id), { read: true });
      });
    batch
      .commit()
      .then(() => {
        addToast('Todas as notificações foram marcadas como lidas.', 'success');
      })
      .catch((err) => {
        console.error('NotificationContext markAllRead error:', err);
        addToast('Não foi possível marcar todas como lidas.', 'error');
      });
  }, [user?.uid, notifications, addToast]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      markAsRead,
      markAllRead,
    }),
    [notifications, unreadCount, markAsRead, markAllRead]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;
